import DOMPurify from 'dompurify';
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';

import { Frame } from './EmailPreviewFrame.StyledComponents';

interface EmailPreviewFrameProps {
    html: string;
}

// The sandbox allows only the same origin, so ORP can read the email's height. Without
// allow-scripts the email still runs no scripts, forms, popups or navigation.
export const EmailPreviewFrame = ({ html }: EmailPreviewFrameProps) => {
    const safeHtml = useMemo(() => DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true }), [html]);
    const [height, setHeight] = useState<number>();
    const observerRef = useRef<ResizeObserver | null>(null);

    useEffect(() => () => observerRef.current?.disconnect(), []);

    const handleLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
        const document = event.currentTarget.contentDocument;
        if (!document) return;

        // Hidden overflow keeps a scroll bar from flashing before the frame grows to fit.
        document.documentElement.style.overflow = 'hidden';
        if (document.body) document.body.style.overflow = 'hidden';

        // Images loading or the window narrowing change the height after the first measure.
        const measure = () => setHeight(document.documentElement.scrollHeight);
        measure();
        observerRef.current?.disconnect();
        observerRef.current = new ResizeObserver(measure);
        observerRef.current.observe(document.body ?? document.documentElement);
    };

    return (
        <Frame
            data-testid="email-preview-frame"
            sandbox="allow-same-origin"
            scrolling="no"
            title="Email preview"
            srcDoc={safeHtml}
            $height={height}
            onLoad={handleLoad}
        />
    );
};
